import {
  FEEDBACK_MAX_BREADCRUMBS,
  type AuthenticatedUser,
  type FeedbackBreadcrumb,
  type FeedbackDiagnostics,
} from '@erp/shared';
import type { Theme } from '@/lib/theme';

const breadcrumbs: FeedbackBreadcrumb[] = [];
const SENSITIVE_QUERY_KEY =
  /(?:token|secret|password|passcode|authorization|auth|code|signature|jwt|session|customer-mode|verification|verify|invite|reset|magic|handoff)/iu;
const SENSITIVE_PATH_PARENT =
  /(?:token|secret|password|passcode|code|signature|jwt|session|customer-mode|verification|verify|invite|reset|magic|handoff)/iu;
const SAFE_QUERY_KEYS = new Set([
  'page',
  'pageSize',
  'sort',
  'tab',
  'view',
  'status',
  'type',
  'category',
  'unresolvedOnly',
  'help',
  'helpAnchor',
]);

interface NavigatorWithDiagnostics extends Navigator {
  readonly connection?: { readonly effectiveType?: string | undefined } | undefined;
  readonly deviceMemory?: number | undefined;
  readonly standalone?: boolean | undefined;
}

function safeIdentifier(value: string, fallback: string): string {
  const normalized = value
    .trim()
    .replace(/[^a-zA-Z0-9_.:/-]+/gu, '-')
    .slice(0, 120);
  return /^[a-zA-Z0-9]/u.test(normalized) ? normalized : fallback;
}

function safePath(path: string): string {
  const withoutQuery = path.split('?')[0] ?? '/';
  return withoutQuery
    .split('/')
    .map((segment, index, all) => {
      const previous = all[index - 1] ?? '';
      if (
        SENSITIVE_PATH_PARENT.test(previous) ||
        segment.length > 80 ||
        /^[A-Za-z0-9_-]{40,}$/u.test(segment)
      ) {
        return '[redacted]';
      }
      return segment;
    })
    .join('/')
    .slice(0, 500);
}

function featureOf(pathname: string): string {
  return safeIdentifier(pathname.split('/').filter(Boolean)[0] ?? 'dashboard', 'dashboard');
}

export function recordFeedbackBreadcrumb(
  input: Omit<FeedbackBreadcrumb, 'timestamp' | 'route' | 'feature'> & {
    readonly timestamp?: string | undefined;
    readonly route?: string | undefined;
    readonly feature?: string | undefined;
  },
): void {
  if (
    window.location.pathname.includes('/field/runs/') &&
    window.location.pathname.endsWith('/customer')
  ) {
    return;
  }
  const route = safePath(input.route ?? window.location.pathname);
  breadcrumbs.push({
    timestamp: input.timestamp ?? new Date().toISOString(),
    type: input.type,
    route,
    feature: safeIdentifier(input.feature ?? featureOf(route), 'unknown'),
    actionId: safeIdentifier(input.actionId, 'unknown.action'),
    actionLabelKey:
      input.actionLabelKey === null ? null : safeIdentifier(input.actionLabelKey, 'unknown.action'),
    entityType: input.entityType === null ? null : safeIdentifier(input.entityType, 'unknown'),
    entityId:
      input.entityId !== null && /^[A-Za-z0-9_-]{1,64}$/u.test(input.entityId)
        ? input.entityId
        : null,
    result: input.result,
    errorTrackingId: input.errorTrackingId,
  });
  if (breadcrumbs.length > FEEDBACK_MAX_BREADCRUMBS) {
    breadcrumbs.splice(0, breadcrumbs.length - FEEDBACK_MAX_BREADCRUMBS);
  }
}

export function currentFeedbackBreadcrumbs(): readonly FeedbackBreadcrumb[] {
  return breadcrumbs.slice(-FEEDBACK_MAX_BREADCRUMBS);
}

export function trackFeedbackRoute(pathname: string, search: string): void {
  recordFeedbackBreadcrumb({
    type: 'NAVIGATION',
    route: pathname,
    feature: featureOf(pathname),
    actionId: 'route.change',
    actionLabelKey: 'diagnostics.actions.routeChange',
    entityType: null,
    entityId: null,
    result: 'SUCCESS',
    errorTrackingId: null,
  });
  if (new URLSearchParams(search).has('tab')) {
    recordFeedbackBreadcrumb({
      type: 'TAB',
      route: pathname,
      feature: featureOf(pathname),
      actionId: 'tab.change',
      actionLabelKey: 'diagnostics.actions.tabChange',
      entityType: null,
      entityId: null,
      result: 'SUCCESS',
      errorTrackingId: null,
    });
  }
}

function routeContext(pathname: string): {
  routePattern: string;
  entityType: string | null;
  entityId: string | null;
} {
  const routes: readonly {
    pattern: RegExp;
    routePattern: string;
    entityType: string | null;
  }[] = [
    { pattern: /^\/partners\/([^/]+)$/u, routePattern: '/partners/:id', entityType: 'PARTNER' },
    { pattern: /^\/products\/([^/]+)$/u, routePattern: '/products/:id', entityType: 'PRODUCT' },
    { pattern: /^\/documents\/([^/]+)/u, routePattern: '/documents/:id', entityType: 'DOCUMENT' },
    {
      pattern: /^\/procurement\/orders\/([^/]+)/u,
      routePattern: '/procurement/orders/:id',
      entityType: 'PURCHASE_ORDER',
    },
    {
      pattern: /^\/field\/runs\/([^/]+)/u,
      routePattern: '/field/runs/:id',
      entityType: 'QUESTIONNAIRE_RUN',
    },
  ];
  for (const route of routes) {
    const match = route.pattern.exec(pathname);
    if (match !== null) {
      const candidate = match[1] ?? null;
      return {
        routePattern: route.routePattern,
        entityType: route.entityType,
        entityId: candidate !== null && /^[A-Za-z0-9_-]{1,64}$/u.test(candidate) ? candidate : null,
      };
    }
  }
  return { routePattern: pathname, entityType: null, entityId: null };
}

function browserDetails(userAgent: string): {
  browserName: string;
  browserVersion: string;
  operatingSystem: string;
  deviceType: 'DESKTOP' | 'TABLET' | 'MOBILE' | 'UNKNOWN';
} {
  const browser =
    /Edg\/([\d.]+)/u.exec(userAgent) ??
    /Chrome\/([\d.]+)/u.exec(userAgent) ??
    /Firefox\/([\d.]+)/u.exec(userAgent) ??
    /Version\/([\d.]+).*Safari/u.exec(userAgent);
  const browserName = userAgent.includes('Edg/')
    ? 'Edge'
    : userAgent.includes('Chrome/')
      ? 'Chrome'
      : userAgent.includes('Firefox/')
        ? 'Firefox'
        : userAgent.includes('Safari/')
          ? 'Safari'
          : 'Unknown';
  const operatingSystem = /Windows NT/u.test(userAgent)
    ? 'Windows'
    : /Android/u.test(userAgent)
      ? 'Android'
      : /iPhone|iPad|iPod/u.test(userAgent)
        ? 'iOS/iPadOS'
        : /Mac OS X/u.test(userAgent)
          ? 'macOS'
          : /Linux/u.test(userAgent)
            ? 'Linux'
            : 'Unknown';
  const deviceType = /Mobile|iPhone|Android/u.test(userAgent)
    ? 'MOBILE'
    : /iPad|Tablet/u.test(userAgent)
      ? 'TABLET'
      : userAgent.length > 0
        ? 'DESKTOP'
        : 'UNKNOWN';
  return {
    browserName,
    browserVersion: browser?.[1] ?? 'Unknown',
    operatingSystem,
    deviceType,
  };
}

export function collectFeedbackDiagnostics(input: {
  readonly session: AuthenticatedUser;
  readonly theme: Theme;
  readonly includedSections: readonly FeedbackDiagnostics['includedSections'][number][];
}): FeedbackDiagnostics {
  const included = new Set(input.includedSections);
  const location = window.location;
  const route = routeContext(location.pathname);
  const search = new URLSearchParams(location.search);
  const navigatorDetails = navigator as NavigatorWithDiagnostics;
  const userAgent = navigator.userAgent;
  const browser = browserDetails(userAgent);
  const internalReferrer = (() => {
    if (document.referrer.length === 0) return null;
    try {
      const referrer = new URL(document.referrer);
      return referrer.origin === location.origin ? safePath(referrer.pathname) : null;
    } catch {
      return null;
    }
  })();

  return {
    includedSections: [...input.includedSections],
    application: included.has('APPLICATION')
      ? {
          capturedAtUtc: new Date().toISOString(),
          localTime: new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium',
            timeStyle: 'long',
          }).format(new Date()),
          localTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          appVersion: import.meta.env.VITE_APP_VERSION ?? '0.1.0',
          buildCommit: import.meta.env.VITE_GIT_COMMIT ?? null,
          apiVersion: 'v1',
          deploymentEnvironment: import.meta.env.VITE_DEPLOYMENT_ENV ?? import.meta.env.MODE,
          frontendBuildTime: import.meta.env.VITE_BUILD_TIMESTAMP ?? null,
          featureFlags: [],
        }
      : null,
    page: included.has('PAGE')
      ? {
          pathname: safePath(location.pathname),
          queryParameters: [...search.entries()]
            .filter(([key]) => SAFE_QUERY_KEYS.has(key) && !SENSITIVE_QUERY_KEY.test(key))
            .slice(0, 20)
            .map(([key, value]) => ({
              key: safeIdentifier(key, 'unknown'),
              value: value.slice(0, 100),
            })),
          routePattern: safePath(route.routePattern),
          pageTitle: document.title.slice(0, 200),
          helpArticleId: search.get('help'),
          activeTab: search.has('tab') ? safeIdentifier(search.get('tab') ?? '', 'unknown') : null,
          businessArea: featureOf(location.pathname),
          entityType: route.entityType,
          entityId: route.entityId,
          internalReferrer,
        }
      : null,
    browser: included.has('BROWSER')
      ? {
          userAgent,
          ...browser,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
          devicePixelRatio: window.devicePixelRatio,
          touchSupported: navigator.maxTouchPoints > 0,
          preferredColorScheme: window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'DARK'
            : 'LIGHT',
          erpTheme: input.theme,
          browserLanguage: navigator.language,
          erpLanguage: input.session.locale,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          online: navigator.onLine,
          connectionType: navigatorDetails.connection?.effectiveType ?? null,
          standalone:
            window.matchMedia('(display-mode: standalone)').matches ||
            navigatorDetails.standalone === true,
          deviceMemoryGb: navigatorDetails.deviceMemory ?? null,
        }
      : null,
    breadcrumbs: included.has('BREADCRUMBS') ? [...currentFeedbackBreadcrumbs()] : [],
  };
}

export function diagnosticServerContextPreview(
  session: AuthenticatedUser,
): Record<string, unknown> {
  return {
    tenantId: session.tenantId,
    tenantName: session.tenantName,
    tenantSlug: session.tenantSlug,
    userId: session.id,
    userName: session.displayName,
    userRole: session.roleKey,
    permissionKeys: session.permissions,
    userLocale: session.locale,
    source: 'verified-session',
  };
}
